import { OpstartplanActie } from "@/entities/actie";
import { mapFormDataToTrein, Treinnummer, Treinserie, TreinserieFormData } from "@/entities/opstartplan";
import { OpstartplanActieDialogContext } from "@/features/acties/components/dialogs/opstartplan-actie-dialog/opstartplan-actie-dialog-context.ts";
import { dismissToast, toastWithUndo } from "@/shared/components";
import { ReactNode, useMemo, useState } from "react";

type OpstartplanActieDialogProviderProps = {
    children: ReactNode;
    actie: OpstartplanActie;
};

export function OpstartplanActieDialogProvider({ children, actie }: Readonly<OpstartplanActieDialogProviderProps>) {
    const [editableTreinseries, setEditableTreinseries] = useState<Treinserie[]>(actie.nieuweWaarde);

    const updateTreinseries = (serie: number, updateFn: (treinnummers: Treinnummer[]) => Treinnummer[]) => {
        setEditableTreinseries((treinseries) => {
            const serieExists = treinseries.some((ts) => ts.serie === serie);
            const newTreinseries = !serieExists
                ? [...treinseries, { serie, treinnummers: updateFn([]) }]
                : treinseries.map((ts) => (ts.serie === serie ? { ...ts, treinnummers: updateFn(ts.treinnummers) } : ts));

            return newTreinseries.filter((ts) => ts.treinnummers.length > 0).sort(Treinserie.sort);
        });
    };

    const updateTreinnummer = (data: TreinserieFormData, serie: number) => {
        updateTreinseries(serie, (treinnummers) =>
            treinnummers.map((tn) => (tn.nummer === Number(data.nummer) ? mapFormDataToTrein(data) : tn))
        );
    };

    const addTreinnummer = (treinnummer: Treinnummer, serie: number) => {
        updateTreinseries(serie, (treinnummers) => [...treinnummers, treinnummer].sort(Treinnummer.sort));
    };

    const getTreinnummer = (serie: number, nummer: number) => {
        return (
            editableTreinseries
                .flatMap((treinserie) => (treinserie.serie === serie ? treinserie.treinnummers : null))
                .find((treinnummer) => treinnummer?.nummer === nummer) ?? null
        );
    };

    const deleteTreinnummer = (serie: number, nummer: number) => {
        const existingTreinnummer = getTreinnummer(serie, nummer);
        updateTreinseries(serie, (treinnummers) => treinnummers.filter((tn) => tn.nummer !== nummer));

        const toastId = toastWithUndo(`Trein ${nummer} verwijderd`, undefined, () => {
            if (existingTreinnummer) {
                addTreinnummer(existingTreinnummer, serie);
                dismissToast(toastId);
            }
        });
    };

    const resetAll = () => {
        setEditableTreinseries(actie.nieuweWaarde);
    };

    const editableActie = useMemo(() => {
        return {
            ...actie,
            nieuweWaarde: editableTreinseries,
        };
    }, [actie, editableTreinseries]);

    return (
        <OpstartplanActieDialogContext value={{ actie: editableActie, updateTreinnummer, deleteTreinnummer, resetAll }}>
            {children}
        </OpstartplanActieDialogContext>
    );
}
